import React from 'react';
import GridContainer from 'components/GridContainer';
import { Grid } from '@mui/material';
import FieldRow from 'components/FormElements/FieldRow';
import {
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { numberToCurrencyString, getCompanyLabel } from 'further-ui/utils';
import { AddCircleOutline, Done } from '@mui/icons-material';
import { NavLink } from 'react-router-dom';
import { createShareSplitAddRoute } from 'adminConstants/routes';
import TextField from 'components/FormElements/AppTextInput';
import AppAutocomplete from 'components/FormElements/AppAutocomplete';
import CkEditor from 'components/CkEditor';
import AppRadioButton from 'components/FormElements/AppRadioButton';
import DropzoneComponent from 'components/Dropzone';
import AdditionalMetric from './AdditionalMetric';
import { InvestorReportingMediaType } from 'adminConstants/investorReporting';
import { useReportingCompanyUpdate } from 'hooks/data/investorReporting/useReportingCompanyUpdate';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload from 'hooks/ui/useFileUpload';
import {
  Controller,
  useController,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import useStyles from './styles';
import { UploadType } from 'further-types/files';
import Spacing from 'components/Spacing';

type CompanyUpdateFormValues = {
  companyInformation: Array<{
    media: Array<{
      id?: string;
      title: string;
      url: string;
      key: string;
      preview?: string;
    }>;
    attachments: Array<{
      id?: string;
      title: string;
      url: string;
      preview?: string;
    }>;
  }>;
};

type AttachmentFieldPath = `companyInformation.${number}.attachments`;

type MediaFieldPath = `companyInformation.${number}.media`;

type Props = any;

const CompanyUpdate: React.FC<Props> = ({
  single,
  duplicateEntries,
  investorReportId,
  index,
  control,
  setValue,
  isViewPage,
  enableLoadLastUpdate,
  firmId,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const { error } = useNotification();
  const queryCompanyId = investorReportId ? single.companyId : single._id;
  const { refetch: fetchLastUpdate, isFetching } = useReportingCompanyUpdate({
    params: {
      companyId: queryCompanyId,
      reportingId: investorReportId,
      firmId,
    },
    enabled: false,
  });
  const { uploadFile } = useFileUpload();

  const {
    fields: additionalMetrics,
    remove: removeMetric,
    append: appendMetric,
  } = useFieldArray({
    control,
    name: `companyInformation[${index}][additionalMetrics]`,
  });
  const mediaFieldPath: MediaFieldPath = `companyInformation.${index}.media`;

  const {
    fields: media,
    remove: removeMedia,
    append: appendMedia,
  } = useFieldArray<CompanyUpdateFormValues, MediaFieldPath>({
    control,
    name: mediaFieldPath,
  });
  const { field: mediaType } = useController({
    control,
    name: `companyInformation[${index}][mediaType]`,
  });
  const attachmentFieldPath: AttachmentFieldPath = `companyInformation.${index}.attachments`;

  const {
    fields: attachments,
    remove: removeAttachment,
    append: appendAttachement,
  } = useFieldArray<CompanyUpdateFormValues, AttachmentFieldPath>({
    control,
    name: attachmentFieldPath,
  });
  const copyFromCompanies = useWatch({
    control,
    name: `companyInformation[${index}][copyFromCompanies]`,
  });
  const isCopyingFromAnotherCompany = copyFromCompanies?.length;

  const getLastCompanyDetail = async () => {
    const { data, isSuccess } = await fetchLastUpdate();
    if (!isSuccess) return;
    const lastUpdate = data?.lastUpdate;
    if (lastUpdate) {
      const {
        updateSummary,
        media = [],
        attachments = [],
        mediaType,
        additionalTitle,
        additionalValue,
        additionalMetrics,
        copyFromCompanies = [],
      } = lastUpdate;

      setValue(`companyInformation[${index}][updateSummary]`, updateSummary);
      setValue(`companyInformation[${index}][media]`, media);
      setValue(`companyInformation[${index}][attachments]`, attachments);
      setValue(`companyInformation[${index}][mediaType]`, mediaType);
      setValue(
        `companyInformation[${index}][additionalTitle]`,
        additionalTitle,
      );
      setValue(
        `companyInformation[${index}][additionalValue]`,
        additionalValue,
      );
      setValue(
        `companyInformation[${index}][additionalMetrics]`,
        additionalMetrics,
      );
      setValue(
        `companyInformation[${index}][copyFromCompanies]`,
        copyFromCompanies,
      );
    }
  };

  const handleRemoveAttachment = (deleteIndex) => {
    if (isCopyingFromAnotherCompany) return;
    removeAttachment(deleteIndex);
  };

  const handleAddMetric = () => {
    if (isCopyingFromAnotherCompany) return;
    appendMetric({ key: '', value: '' });
  };

  const handleRemoveMetric = (metricIndex) => {
    if (isCopyingFromAnotherCompany) return;
    removeMetric(metricIndex);
  };

  async function uploadMedia(file) {
    const { data, error: uploadError } = await uploadFile(
      file,
      UploadType.ReportingAttachments,
    );
    if (data) {
      return data.filePath;
    } else {
      error(uploadError.message);
    }
  }

  const handleRemoveMedia = (deleteIndex) => {
    if (isCopyingFromAnotherCompany) return;
    removeMedia(deleteIndex);
  };

  const handleChangeMedia = async (files) => {
    if (isCopyingFromAnotherCompany) return;

    const mediaVideoIndex = media.findIndex(
      (item) => item.key === InvestorReportingMediaType.VIDEO,
    );

    if (mediaVideoIndex > -1) {
      removeMedia(mediaVideoIndex);
    }

    await Promise.all(
      files.map(async (file) => {
        if (file.path) {
          const filePath = await uploadMedia(file);
          if (!filePath) return;
          appendMedia({
            title: file?.name,
            url: filePath,
            key: InvestorReportingMediaType.SLIDESHOW,
            preview: file?.preview,
          });
        }
      }),
    );
  };

  const handleChangeVideo = async (value) => {
    if (isCopyingFromAnotherCompany) return;

    let url = value;

    // Handle youtu.be format
    if (url.includes('youtu.be')) {
      const videoId = url.split('/').pop()?.split('?')[0];
      url = `https://www.youtube.com/embed/${videoId}`;
    }
    // Handle traditional youtube.com format
    else {
      url = url.replace('watch?v=', 'embed/');
    }

    setValue(`companyInformation[${index}][media]`, [
      {
        title: value,
        url,
        key: InvestorReportingMediaType.VIDEO,
      },
    ]);
  };

  const handleChangeAttachment = async (files) => {
    if (isCopyingFromAnotherCompany) return;

    await Promise.all(
      files.map(async (file) => {
        if (file.path) {
          const filePath = await uploadMedia(file);
          if (!filePath) return;
          appendAttachement({
            title: file?.name,
            url: filePath,
            preview: file?.preview,
          });
        }
      }),
    );
  };

  const handleCopyFromCompanies = (event) => {
    const sourceCompany = duplicateEntries[event.target.value];

    if (sourceCompany) {
      if (!copyFromCompanies.includes(sourceCompany.companyId)) {
        setValue(`companyInformation[${index}][copyFromCompanies]`, [
          sourceCompany.companyId,
        ]);
        setValue(
          `companyInformation[${index}][copiedFromAnotherCompany]`,
          true,
        );
      }
    } else {
      setValue(`companyInformation[${index}][copyFromCompanies]`, []);
      setValue(`companyInformation[${index}][copiedFromAnotherCompany]`, false);
    }
  };

  const handleResetCompaniesCopyFrom = () => {
    if (isCopyingFromAnotherCompany) {
      setValue(`companyInformation[${index}][copyFromCompanies]`, []);
      setValue(`companyInformation[${index}][copiedFromAnotherCompany]`, false);
    }
  };

  return (
    <GridContainer style={{ marginTop: '0.5rem' }}>
      <Grid
        size={{
          xs: 12,
          md: 11,
        }}
      >
        <GridContainer>
          <Grid
            size={{
              md: 7,
            }}
          >
            <Spacing>
              <FieldRow title="Company">
                <div className={classes.tradingNameRow}>
                  {getCompanyLabel(single) || '.'}
                </div>
              </FieldRow>
              <FieldRow title="Current share price">
                {numberToCurrencyString(single.currentSharePrice?.[0]?.amount, {
                  unlimitedDp: true,
                })}
              </FieldRow>
              {enableLoadLastUpdate && (
                <div>
                  <Button
                    disabled={
                      isCopyingFromAnotherCompany ||
                      isViewPage ||
                      !single.lastUpdate
                    }
                    loading={isFetching}
                    variant="outlined"
                    size="small"
                    onClick={getLastCompanyDetail}
                    data-testid={`load-last-update`}
                  >
                    Load last update text
                  </Button>
                </div>
              )}
            </Spacing>
          </Grid>
          <Grid
            size={{
              md: 5,
              xs: 12,
            }}
          >
            <Callout />
          </Grid>
        </GridContainer>
        <Spacing>
          {single.disableSharePriceUpdate ? null : (
            <FieldRow
              title="Updated share price"
              informationText="Leave blank if no change"
              tooltipText={
                <span>
                  If there has been a share split since the company's last share
                  price update, please process this in the share split function
                  for this company{' '}
                  <NavLink
                    style={{ color: theme.palette.primary[400] }}
                    target="_blank"
                    to={createShareSplitAddRoute(
                      investorReportId ? single.companyId : single._id,
                    )}
                  >
                    here
                  </NavLink>{' '}
                  before submitting this investor update.
                </span>
              }
            >
              <Controller
                name={`companyInformation[${index}][updatedSharePrice]`}
                control={control}
                render={({
                  field: { ref, ...rest },
                  formState: { errors },
                }) => {
                  return (
                    <TextField
                      inputRef={ref}
                      {...rest}
                      type="number"
                      gbpStartAdornment
                      placeholder="Leave blank if no change"
                      fullWidth
                      data-testid={`updatedSharePrice`}
                      disabled={isViewPage || single.disableSharePriceUpdate}
                      error={
                        !!errors?.companyInformation?.[index]?.updatedSharePrice
                      }
                      helperText={
                        errors?.companyInformation?.[index]?.updatedSharePrice
                          ?.message
                      }
                    />
                  );
                }}
              />
            </FieldRow>
          )}

          {duplicateEntries?.length ? (
            <FieldRow title="Copy from">
              <AppAutocomplete
                options={duplicateEntries}
                value={duplicateEntries.find(
                  ({ companyId }) => copyFromCompanies?.[0] === companyId,
                )}
                //@ts-expect-error
                getOptionLabel={getCompanyLabel}
                getOptionKey={(option) => option?.tradingName ?? ''}
                onChange={handleCopyFromCompanies}
                onReset={handleResetCompaniesCopyFrom}
                data-testid={`copyFrom`}
                disabled={isViewPage}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </FieldRow>
          ) : null}

          {!isCopyingFromAnotherCompany && (
            <>
              {additionalMetrics?.map((metric, metricIndex) => (
                <AdditionalMetric
                  key={`metric_${metricIndex}`}
                  metric={metric}
                  handleRemove={handleRemoveMetric}
                  metricIndex={metricIndex}
                  index={index}
                  disabled={isViewPage}
                  control={control}
                />
              ))}
              <Grid size={12}>
                <div className={classes.addMetricBtnGroup}>
                  <Button
                    disabled={isViewPage}
                    startIcon={<AddCircleOutline />}
                    className={cx(classes.addMetricBtn)}
                    onClick={handleAddMetric}
                    variant="text"
                    color="primary"
                  >
                    Add additional metric
                    <span className={classes.additionalMetricsExtra}>
                      (e.g. ARR growth)
                    </span>
                  </Button>
                </div>
              </Grid>
              <FieldRow
                title="Company description"
                informationText="Any changes to this will also edit the company's main description (shown on the summary page of each investor’s portal)"
                topTitle
              >
                <Controller
                  name={`companyInformation[${index}][description]`}
                  control={control}
                  render={({ field, formState: { errors } }) => (
                    <>
                      <CkEditor
                        disabled={isViewPage}
                        onChange={(value: string) => {
                          setValue(
                            `companyInformation[${index}][description]`,
                            value,
                          );
                        }}
                        value={field.value}
                        onBlur={(value: string) => {
                          setValue(
                            `companyInformation[${index}][description]`,
                            value,
                          );
                        }}
                      />
                      <p
                        className={classes.errorCk}
                        id={`companyInformation[${index}][description]`}
                      >
                        {
                          errors?.companyInformation?.[index]?.description
                            ?.message
                        }
                      </p>
                    </>
                  )}
                />
              </FieldRow>
              <FieldRow title="Company update" topTitle>
                <Controller
                  name={`companyInformation[${index}][updateSummary]`}
                  control={control}
                  render={({ field }) => (
                    <CkEditor
                      placeholder="Want to give a specific update about this company? Use this field to let your investors know what has been happening."
                      onChange={(value: string) => {
                        setValue(
                          `companyInformation[${index}][updateSummary]`,
                          value,
                        );
                      }}
                      value={field.value}
                      disabled={isViewPage}
                      onBlur={(value: string) => {
                        setValue(
                          `companyInformation[${index}][updateSummary]`,
                          value,
                        );
                      }}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Add media">
                <AppRadioButton
                  key="link"
                  name="mediaType"
                  label="Add video"
                  value={InvestorReportingMediaType.VIDEO}
                  checked={mediaType.value === InvestorReportingMediaType.VIDEO}
                  onChange={mediaType.onChange}
                  disabled={isViewPage}
                  data-testid={`mediaType-${InvestorReportingMediaType.VIDEO}`}
                />
                <AppRadioButton
                  key="Sideshow"
                  name="mediaType"
                  label="Slideshow"
                  value={InvestorReportingMediaType.SLIDESHOW}
                  checked={
                    mediaType.value === InvestorReportingMediaType.SLIDESHOW
                  }
                  onChange={mediaType.onChange}
                  disabled={isViewPage}
                  data-testid={`mediaType-${InvestorReportingMediaType.SLIDESHOW}`}
                />
                {mediaType.value === InvestorReportingMediaType.VIDEO ? (
                  <Controller
                    name={`companyInformation[${index}][media]`}
                    control={control}
                    render={({ field: { ref, value, ...rest } }) => (
                      <TextField
                        inputRef={ref}
                        {...rest}
                        value={
                          value?.[0]?.key === InvestorReportingMediaType.VIDEO
                            ? value?.[0]?.url
                            : ''
                        }
                        onChange={(e) => {
                          handleChangeVideo(e.target.value);
                        }}
                        fullWidth
                        placeholder="Enter video link (YouTube only)"
                        disabled={isViewPage}
                        data-testid={`media`}
                      />
                    )}
                  />
                ) : (
                  <DropzoneComponent
                    name="media"
                    defaultAcceptGroup="images"
                    disabled={isViewPage}
                    files={
                      media
                        ?.filter(
                          (item) =>
                            item.key === InvestorReportingMediaType.SLIDESHOW,
                        )
                        .map((file) => ({
                          fileName: file.title,
                          preview: file.preview || file.url,
                        })) ?? []
                    }
                    onDropFile={handleChangeMedia}
                    onRemove={handleRemoveMedia}
                    maxFiles={10}
                  />
                )}
              </FieldRow>

              <FieldRow title="Additional field title">
                <Controller
                  name={`companyInformation[${index}][additionalTitle]`}
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      inputRef={ref}
                      {...rest}
                      disabled={isViewPage}
                      placeholder="E.g. Can you help?"
                      fullWidth
                      data-testid={`additionalTitle`}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Additional field text" topTitle>
                <Controller
                  name={`companyInformation[${index}][additionalValue]`}
                  control={control}
                  render={({ field }) => (
                    <CkEditor
                      placeholder="An optional box for you to use as you see fit. Want to ask your investors to help your company in any way? Is the company running an event that they want to publicise to investors? Let the investors know here."
                      disabled={isViewPage}
                      onChange={(value: string) => {
                        setValue(
                          `companyInformation[${index}][additionalValue]`,
                          value,
                        );
                      }}
                      value={field.value}
                      onBlur={(value: string) => {
                        setValue(
                          `companyInformation[${index}][additionalValue]`,
                          value,
                        );
                      }}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow title="Attach documents">
                {isViewPage ? (
                  attachments.length > 0 ? (
                    <div>
                      {attachments.map((file, fileIndex) => (
                        <div key={fileIndex} style={{ marginBottom: '8px' }}>
                          <a
                            href={file.preview || file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: theme.palette.primary.main,
                            }}
                          >
                            {file.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No documents attached
                    </Typography>
                  )
                ) : (
                  <DropzoneComponent
                    name="attachments"
                    disabled={isViewPage}
                    files={
                      attachments.map((file) => ({
                        fileName: file.title,
                        preview: file.preview || file.url,
                      })) ?? []
                    }
                    onDropFile={handleChangeAttachment}
                    onRemove={handleRemoveAttachment}
                    maxFiles={10}
                    accept={{
                      'application/pdf': ['.pdf'],
                    }}
                  />
                )}
              </FieldRow>
            </>
          )}
        </Spacing>
      </Grid>
    </GridContainer>
  );
};

const Callout = () => {
  const { classes, cx } = useStyles();
  const metrics = [
    'Value at time of subscription',
    'Current value',
    '% change',
  ];
  return (
    <div className={cx(classes.callout)}>
      <Typography className={classes.calloutTitle}>
        What metrics do investors see automatically?
      </Typography>
      <List dense={true}>
        {metrics.map((metric) => (
          <ListItem key={metric} disableGutters>
            <ListItemIcon>
              <Done className={classes.greenIcon} />
            </ListItemIcon>
            <ListItemText primary={metric} />
          </ListItem>
        ))}
      </List>
    </div>
  );
};

export default CompanyUpdate;
